<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div v-if="!this.mapper" class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="flex flex-col space-y-6">
        <!-- Evidence Hero Section -->
        <v-card v-if="!this.mapper" class="mb-6 ev-hero-card" elevation="4">
          <!-- Gradient Header -->
          <div class="ev-hero-header">
            <div class="ev-hero-header-content">
              <div class="d-flex align-center mb-2">
                <v-avatar size="48" class="mr-3 ev-hero-logo">
                  <v-icon size="28" color="white">mdi-fingerprint</v-icon>
                </v-avatar>
                <div>
                  <h1 class="ev-hero-title">Evidence Management</h1>
                  <p class="ev-hero-subtitle">
                    Track, manage &amp; review your compliance evidence
                  </p>
                </div>
              </div>
            </div>
          </div>

          <v-card-text class="pt-6">
            <p class="text-body-1 mb-5 ev-intro-text">
              View, manage, and track all your <strong>evidence items</strong> used to support
              security claims. Review evidence status, examine mappings to sub-claims, and create
              new evidence as needed.
            </p>

            <!-- Workflow Section -->
            <div class="ev-workflow-section mb-6">
              <div class="ev-section-header mb-4">
                <v-icon color="primary" size="28" class="mr-2">mdi-rocket-launch</v-icon>
                <span class="text-h6 font-weight-bold">Evidence Workflow</span>
              </div>

              <v-row class="ev-workflow-steps">
                <!-- Step 1: Create -->
                <v-col cols="12" md="4" class="ev-workflow-col">
                  <div class="ev-ws step-create">
                    <div class="ev-step-number">1</div>
                    <div class="ev-step-icon-wrapper create-icon">
                      <v-icon size="40" color="white">mdi-plus-circle-outline</v-icon>
                    </div>
                    <div class="ev-step-title">Create Evidence</div>
                    <div class="ev-step-description">
                      Submit evidence with a name, description, type, and optional supporting files
                      or tool integrations.
                    </div>
                  </div>
                </v-col>

                <!-- Step 2: Review -->
                <v-col cols="12" md="4" class="ev-workflow-col">
                  <div class="ev-ws step-review">
                    <div class="ev-step-number review-num">2</div>
                    <div class="ev-step-icon-wrapper review-icon">
                      <v-icon size="40" color="white">mdi-clipboard-text-search-outline</v-icon>
                    </div>
                    <div class="ev-step-title">Review &amp; Comment</div>
                    <div class="ev-step-description">
                      Reviewers assess evidence, provide feedback, and approve or request changes to
                      submissions.
                    </div>
                  </div>
                </v-col>

                <!-- Step 3: Approve -->
                <v-col cols="12" md="4" class="ev-workflow-col">
                  <div class="ev-ws step-approve">
                    <div class="ev-step-number approve-num">3</div>
                    <div class="ev-step-icon-wrapper approve-icon">
                      <v-icon size="40" color="white">mdi-check-decagram</v-icon>
                    </div>
                    <div class="ev-step-title">Approve &amp; Map</div>
                    <div class="ev-step-description">
                      Approved evidence is mapped to sub-claims and contributes to your security
                      assurance case.
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-5"></v-divider>

            <!-- Features Grid -->
            <v-row>
              <v-col cols="12" md="6">
                <div class="ev-feature-box">
                  <div class="ev-feature-box-header">
                    <v-icon color="primary" size="28" class="mr-2">mdi-clipboard-list</v-icon>
                    <span class="text-h6 font-weight-bold">Evidence Management</span>
                  </div>
                  <p class="text-body-2 grey--text text--darken-1 mt-2 mb-3">
                    Centralized management for all your compliance evidence.
                  </p>
                  <ul class="ev-feature-list">
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      View all evidence items in one location
                    </li>
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      Examine sub-claim mappings for each evidence
                    </li>
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      Filter and sort by multiple criteria
                    </li>
                    <li>
                      <v-icon small color="primary" class="mr-2">mdi-check-circle</v-icon>
                      View evidence source (Manual, Threat Spider, Dependency Track)
                    </li>
                  </ul>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="ev-feature-box lifecycle-box">
                  <div class="ev-feature-box-header">
                    <v-icon color="success" size="28" class="mr-2"
                      >mdi-checkbox-marked-circle</v-icon
                    >
                    <span class="text-h6 font-weight-bold">Evidence Lifecycle</span>
                  </div>
                  <p class="text-body-2 grey--text text--darken-1 mt-2 mb-3">
                    Track each evidence item through its complete lifecycle.
                  </p>
                  <ul class="ev-feature-list">
                    <li>
                      <v-chip x-small color="warning" class="mr-2">Draft</v-chip>
                      Initial state without file uploads
                    </li>
                    <li>
                      <v-chip x-small color="primary" class="mr-2">Pending</v-chip>
                      Evidence awaiting review
                    </li>
                    <li>
                      <v-chip x-small color="blue" class="mr-2">Reviewed</v-chip>
                      Evidence with reviewer comments
                    </li>
                    <li>
                      <v-chip x-small color="error" class="mr-2">Rejected</v-chip>
                      Evidence requiring improvements
                    </li>
                    <li>
                      <v-chip x-small color="success" class="mr-2">Approved</v-chip>
                      Evidence meeting all requirements
                    </li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <!-- Summary Metric Cards -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" lg="3">
            <v-card class="ev-metric-card ev-primary-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-file-document-multiple</v-icon>
                <div class="text-h3 font-weight-bold">{{ evidences.length }}</div>
                <div class="text-subtitle-1">Total Evidence</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card class="ev-metric-card ev-secondary-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-account-edit</v-icon>
                <div class="text-h3 font-weight-bold">{{ regularEvidence.length }}</div>
                <div class="text-subtitle-1">Regular Evidence</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card class="ev-metric-card ev-accent-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-spider</v-icon>
                <div class="text-h3 font-weight-bold">{{ threatSpiderEvidence.length }}</div>
                <div class="text-subtitle-1">Threat Spider</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-card class="ev-metric-card ev-success-grad" dark>
              <v-card-text class="d-flex flex-column align-center py-6">
                <v-icon size="48" class="mb-2">mdi-check-decagram</v-icon>
                <div class="text-h3 font-weight-bold">
                  {{ evidences.filter(e => e.status === 'Approved').length }}
                </div>
                <div class="text-subtitle-1">Approved</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div class="ev-content-wrapper">
          <div class="ev-search-bar-container">
            <div class="ev-search-bar-header">
              <v-icon color="white" size="20" class="mr-2">mdi-magnify</v-icon>
              <span class="white--text font-weight-bold">Search &amp; Filter</span>
            </div>
            <div class="ev-search-bar-body d-flex align-center flex-wrap">
              <v-select
                v-model="searchField"
                :items="searchFields"
                item-text="text"
                item-value="value"
                :label="$t('search-field')"
                hide-details
                outlined
                dense
                class="search-field"
                style="max-width: 200px;"
              ></v-select>
              <v-text-field
                v-model="searchQuery"
                append-icon="mdi-magnify"
                :label="$t('search')"
                hide-details
                class="flex-grow-1"
                outlined
                dense
                @keyup.enter="applySearch"
              />
              <v-btn
                v-if="!isReviewer"
                class="ev-create-btn"
                color="primary"
                @click="$router.push({ name: 'EvidenceBuilder' })"
              >
                <v-icon class="mr-2">mdi-plus</v-icon>
                {{ $t('evidence.create') }}
              </v-btn>
            </div>
          </div>

          <!-- Evidence Tabs -->
          <v-card class="ev-tabs-card" elevation="3">
            <v-tabs v-model="activeTab" class="ev-tabs" dark>
              <v-tab>
                <v-icon left small>mdi-file-document-outline</v-icon>
                Regular Evidence
              </v-tab>
              <v-tab>
                <v-icon left small>mdi-spider</v-icon>
                Threat Spider Evidence
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- Regular Evidence Tab -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-4">
                      <div class="text-h6">Regular Evidence Overview</div>
                      <v-chip color="primary" dark class="ml-2">
                        Total: {{ filteredRegularEvidence.length }}
                      </v-chip>
                    </div>

                    <!-- Sort Controls -->
                    <div class="sort-controls mb-4">
                      <v-btn-toggle v-model="sortField" mandatory>
                        <v-btn value="updatedAt" small>
                          Update Date
                          <v-icon right>{{ getSortIcon('updatedAt') }}</v-icon>
                        </v-btn>
                        <v-btn value="createdAt" small>
                          Creation Date
                          <v-icon right>{{ getSortIcon('createdAt') }}</v-icon>
                        </v-btn>
                        <v-btn value="status" small>
                          Status
                          <v-icon right>{{ getSortIcon('status') }}</v-icon>
                        </v-btn>
                      </v-btn-toggle>
                    </div>

                    <v-row>
                      <v-col
                        v-for="evidence in paginatedRegularEvidence"
                        :key="evidence.id"
                        cols="12"
                        md="6"
                        lg="4"
                      >
                        <v-card outlined class="evidence-card">
                          <v-card-title class="d-flex justify-space-between align-start">
                            <div class="evidence-title">{{ evidence.title }}</div>
                            <div class="d-flex flex-column align-end" style="gap:4px;">
                              <v-chip :color="statusChipColor(evidence.status)" dark small>
                                {{ evidence.status }}
                              </v-chip>
                              <v-chip
                                v-if="evidence.complianceScore != null"
                                x-small
                                outlined
                                :color="
                                  evidence.complianceScore >= 4
                                    ? 'green'
                                    : evidence.complianceScore >= 3
                                    ? 'amber darken-2'
                                    : evidence.complianceScore >= 1
                                    ? 'deep-orange'
                                    : 'red'
                                "
                              >
                                <v-icon x-small left>mdi-shield-check-outline</v-icon>
                                {{ evidence.complianceScore }}/5
                              </v-chip>
                            </div>
                          </v-card-title>
                          <v-card-text>
                            <div class="evidence-details">
                              <div class="text-caption text-grey-600">ID: {{ evidence.id }}</div>
                              <div class="text-body-2 mt-2">{{ evidence.description }}</div>
                              <div class="text-body-2 mt-2">
                                <strong>Mapped Sub-Claims:</strong>
                                {{ evidence.subClaimsDto ? evidence.subClaimsDto.length : '0' }}
                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      class="ml-5"
                                      icon
                                      v-on="on"
                                      v-bind="attrs"
                                      @click="showSubClaimsDialog(evidence)"
                                    >
                                      <v-icon medium>mdi-eye</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>{{ $t('preview') }}</span>
                                </v-tooltip>
                              </div>
                              <div class="text-body-2 mt-2">
                                <strong>Mapped SoCs:</strong>
                                <span v-if="evidence.socIds && evidence.socIds.length > 0">
                                  {{ evidence.socIds.length }}
                                </span>
                                <span v-else class="grey--text">N/A</span>
                                <v-tooltip top v-if="evidence.socIds && evidence.socIds.length > 0">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      class="ml-2"
                                      icon
                                      v-on="on"
                                      v-bind="attrs"
                                      @click="showSocsDialog(evidence)"
                                    >
                                      <v-icon small>mdi-eye</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>View mapped SoCs</span>
                                </v-tooltip>
                              </div>
                              <div class="text-body-2 mt-2">
                                <strong>Source:</strong>
                                <v-chip
                                  x-small
                                  outlined
                                  :color="getSourceColor(evidence.source)"
                                  class="ml-1"
                                >
                                  {{ evidence.source || 'Manual' }}
                                </v-chip>
                              </div>
                              <div class="mt-3">
                                <v-chip small outlined>{{ evidence.type }}</v-chip>
                                <v-chip small outlined class="ml-2">v{{ evidence.version }}</v-chip>
                              </div>
                              <div class="text-caption text-grey-600 mt-2">
                                <strong>Created:</strong> {{ formatDate(evidence.createdAt) }}
                              </div>
                              <div class="text-caption text-grey-600">
                                <strong>Updated:</strong> {{ formatDate(evidence.updatedAt) }}
                              </div>
                            </div>
                          </v-card-text>
                          <v-card-actions class="justify-center">
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-badge
                                  :value="hasComments(evidence)"
                                  color="warning"
                                  overlap
                                  icon="mdi-comment-multiple"
                                  offset-x="10"
                                  offset-y="10"
                                >
                                  <v-btn
                                    icon
                                    color="info"
                                    v-on="on"
                                    v-bind="attrs"
                                    @click="showEvidenceDetailsDialog(evidence)"
                                  >
                                    <v-icon>mdi-eye</v-icon>
                                  </v-btn>
                                </v-badge>
                              </template>
                              <span>
                                {{ $t('preview') }} Details
                                <span v-if="hasComments(evidence)" class="font-weight-bold">
                                  - Has Comments
                                </span>
                              </span>
                            </v-tooltip>
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  color="red"
                                  v-on="on"
                                  v-bind="attrs"
                                  @click="enableDeleteDialog(evidence)"
                                >
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('delete.message') }}</span>
                            </v-tooltip>
                            <v-tooltip
                              top
                              v-if="
                                isCertifaiUser &&
                                  evidence.status !== 'Approved' &&
                                  evidence.status !== 'Rejected'
                              "
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  v-on="on"
                                  v-bind="attrs"
                                  @click="editEvidence(evidence)"
                                >
                                  <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('edit') }}</span>
                            </v-tooltip>
                            <v-tooltip
                              top
                              v-if="
                                isReviewer &&
                                  evidence.status !== 'Approved' &&
                                  evidence.status !== 'Draft' &&
                                  evidence.status !== 'Rejected' &&
                                  evidence.status !== 'Reviewed' &&
                                  evidence.status !== 'Largely Compliant'
                              "
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  color="green"
                                  v-on="on"
                                  v-bind="attrs"
                                  @click="enableApproveDialog(evidence)"
                                >
                                  <v-icon>mdi-clipboard-text-search</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('review.message') }}</span>
                            </v-tooltip>
                            <div v-if="mapper" class="ml-auto d-flex align-center">
                              <v-chip
                                v-if="isItemDisabled(evidence)"
                                small
                                color="success"
                                text-color="white"
                                class="mr-2 ev-selected-chip"
                              >
                                <v-icon small left>mdi-check-circle</v-icon>
                                Selected
                              </v-chip>
                              <v-switch
                                :disabled="isItemDisabled(evidence)"
                                :input-value="isItemDisabled(evidence)"
                                @change="toggleSelection(evidence)"
                                color="success"
                                dense
                                class="ev-mapper-switch"
                              />
                            </div>
                          </v-card-actions>
                        </v-card>
                      </v-col>
                    </v-row>
                    <v-pagination
                      v-model="regularEvidencePage"
                      :length="Math.ceil(filteredRegularEvidence.length / itemsPerPage)"
                      :total-visible="7"
                      class="mt-4"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- Threat Spider Evidence Tab -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <div class="d-flex justify-space-between align-center mb-4">
                      <div class="text-h6">Threat Spider Evidence Overview</div>
                      <v-chip color="secondary" dark class="ml-2">
                        Total: {{ filteredThreatSpiderEvidence.length }}
                      </v-chip>
                    </div>

                    <!-- Sort Controls for Threat Spider -->
                    <div class="sort-controls mb-4">
                      <v-btn-toggle v-model="sortField" mandatory>
                        <v-btn value="updatedAt" small>
                          Update Date
                          <v-icon right>{{ getSortIcon('updatedAt') }}</v-icon>
                        </v-btn>
                        <v-btn value="createdAt" small>
                          Creation Date
                          <v-icon right>{{ getSortIcon('createdAt') }}</v-icon>
                        </v-btn>
                        <v-btn value="status" small>
                          Status
                          <v-icon right>{{ getSortIcon('status') }}</v-icon>
                        </v-btn>
                      </v-btn-toggle>
                    </div>

                    <v-row>
                      <v-col
                        v-for="evidence in paginatedThreatSpiderEvidence"
                        :key="evidence.id"
                        cols="12"
                        md="6"
                        lg="4"
                      >
                        <v-card outlined class="evidence-card threat-spider-card">
                          <v-card-title class="d-flex justify-space-between align-start">
                            <div class="evidence-title">{{ evidence.title }}</div>
                            <div class="d-flex flex-column align-end" style="gap:4px;">
                              <v-chip :color="statusChipColor(evidence.status)" dark small>
                                {{ evidence.status }}
                              </v-chip>
                              <v-chip
                                v-if="evidence.complianceScore != null"
                                x-small
                                outlined
                                :color="
                                  evidence.complianceScore >= 4
                                    ? 'green'
                                    : evidence.complianceScore >= 3
                                    ? 'amber darken-2'
                                    : evidence.complianceScore >= 1
                                    ? 'deep-orange'
                                    : 'red'
                                "
                              >
                                <v-icon x-small left>mdi-shield-check-outline</v-icon>
                                {{ evidence.complianceScore }}/5
                              </v-chip>
                            </div>
                          </v-card-title>
                          <v-card-text>
                            <div class="evidence-details">
                              <div class="text-caption text-grey-600">ID: {{ evidence.id }}</div>
                              <div class="text-body-2 mt-2">{{ evidence.description }}</div>
                              <div class="text-body-2 mt-2">
                                <strong>Mapped Sub-Claims:</strong>
                                {{ evidence.subClaimsDto ? evidence.subClaimsDto.length : '0' }}
                                <v-tooltip top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      class="ml-5"
                                      icon
                                      v-on="on"
                                      v-bind="attrs"
                                      @click="showSubClaimsDialog(evidence)"
                                    >
                                      <v-icon medium>mdi-eye</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>{{ $t('preview') }}</span>
                                </v-tooltip>
                              </div>
                              <div class="text-body-2 mt-2">
                                <strong>Mapped SoCs:</strong>
                                <span v-if="evidence.socIds && evidence.socIds.length > 0">
                                  {{ evidence.socIds.length }}
                                </span>
                                <span v-else class="grey--text">N/A</span>
                                <v-tooltip top v-if="evidence.socIds && evidence.socIds.length > 0">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      class="ml-2"
                                      icon
                                      v-on="on"
                                      v-bind="attrs"
                                      @click="showSocsDialog(evidence)"
                                    >
                                      <v-icon small>mdi-eye</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>View mapped SoCs</span>
                                </v-tooltip>
                              </div>
                              <div class="text-body-2 mt-2">
                                <strong>Source:</strong>
                                <v-chip
                                  x-small
                                  outlined
                                  :color="getSourceColor(evidence.source)"
                                  class="ml-1"
                                >
                                  {{ evidence.source || 'Manual' }}
                                </v-chip>
                              </div>
                              <div class="mt-3">
                                <v-chip small outlined color="purple">{{ evidence.type }}</v-chip>
                                <v-chip small outlined class="ml-2">v{{ evidence.version }}</v-chip>
                              </div>
                              <div class="text-caption text-grey-600 mt-2">
                                <strong>Created:</strong> {{ formatDate(evidence.createdAt) }}
                              </div>
                              <div class="text-caption text-grey-600">
                                <strong>Updated:</strong> {{ formatDate(evidence.updatedAt) }}
                              </div>
                            </div>
                          </v-card-text>
                          <v-card-actions class="justify-center">
                            <!-- Same action buttons as regular evidence -->
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-badge
                                  :value="hasComments(evidence)"
                                  color="warning"
                                  overlap
                                  icon="mdi-comment-multiple"
                                  offset-x="10"
                                  offset-y="10"
                                >
                                  <v-btn
                                    icon
                                    color="info"
                                    v-on="on"
                                    v-bind="attrs"
                                    @click="showEvidenceDetailsDialog(evidence)"
                                  >
                                    <v-icon>mdi-eye</v-icon>
                                  </v-btn>
                                </v-badge>
                              </template>
                              <span>
                                {{ $t('preview') }} Details
                                <span v-if="hasComments(evidence)" class="font-weight-bold">
                                  - Has Comments
                                </span>
                              </span>
                            </v-tooltip>
                            <v-tooltip top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  color="red"
                                  v-on="on"
                                  v-bind="attrs"
                                  @click="enableDeleteDialog(evidence)"
                                >
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('delete.message') }}</span>
                            </v-tooltip>
                            <v-tooltip
                              top
                              v-if="
                                isCertifaiUser &&
                                  evidence.status !== 'Approved' &&
                                  evidence.status !== 'Rejected'
                              "
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  v-on="on"
                                  v-bind="attrs"
                                  @click="editEvidence(evidence)"
                                >
                                  <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('edit') }}</span>
                            </v-tooltip>
                            <v-tooltip
                              top
                              v-if="
                                isReviewer &&
                                  evidence.status !== 'Approved' &&
                                  evidence.status !== 'Draft' &&
                                  evidence.status !== 'Rejected' &&
                                  evidence.status !== 'Reviewed' &&
                                  evidence.status !== 'Largely Compliant'
                              "
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  color="green"
                                  v-on="on"
                                  v-bind="attrs"
                                  @click="enableApproveDialog(evidence)"
                                >
                                  <v-icon>mdi-clipboard-text-search</v-icon>
                                </v-btn>
                              </template>
                              <span>{{ $t('review.message') }}</span>
                            </v-tooltip>
                            <div v-if="mapper" class="ml-auto d-flex align-center">
                              <v-chip
                                v-if="isItemDisabled(evidence)"
                                small
                                color="success"
                                text-color="white"
                                class="mr-2 ev-selected-chip"
                              >
                                <v-icon small left>mdi-check-circle</v-icon>
                                Selected
                              </v-chip>
                              <v-switch
                                :disabled="isItemDisabled(evidence)"
                                :input-value="isItemDisabled(evidence)"
                                @change="toggleSelection(evidence)"
                                color="success"
                                dense
                                class="ev-mapper-switch"
                              />
                            </div>
                          </v-card-actions>
                        </v-card>
                      </v-col>
                    </v-row>
                    <v-pagination
                      v-model="threatSpiderEvidencePage"
                      :length="Math.ceil(filteredThreatSpiderEvidence.length / itemsPerPage)"
                      :total-visible="7"
                      class="mt-4"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>

          <!-- Evidence Details Dialog -->
          <v-dialog v-model="evidenceDetailsDialog" max-width="800" scrollable>
            <v-card class="evidence-details-card">
              <v-card-title class="evidence-details-header">
                <v-icon left color="white" size="28">mdi-file-document-outline</v-icon>
                <span class="evidence-details-title">Evidence Details</span>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="evidenceDetailsDialog = false" class="close-btn">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-divider></v-divider>

              <v-card-text v-if="selectedEvidenceForDetails" class="evidence-details-content">
                <!-- Header Section with ID and Status -->
                <div class="details-header-section">
                  <div class="detail-card id-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-identifier</v-icon>
                      Evidence ID
                    </div>
                    <div class="detail-card-value">
                      {{ selectedEvidenceForDetails.id || 'N/A' }}
                    </div>
                  </div>

                  <div class="detail-card status-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-information</v-icon>
                      Status
                    </div>
                    <div class="detail-card-value">
                      <v-chip
                        v-if="selectedEvidenceForDetails.status"
                        :color="statusChipColor(selectedEvidenceForDetails.status)"
                        dark
                        class="status-chip-large"
                      >
                        <v-icon left small>{{
                          selectedEvidenceForDetails.status === 'Rejected'
                            ? 'mdi-close-circle'
                            : selectedEvidenceForDetails.status === 'Approved'
                            ? 'mdi-check-circle'
                            : selectedEvidenceForDetails.status === 'Largely Compliant'
                            ? 'mdi-check-decagram-outline'
                            : selectedEvidenceForDetails.status === 'Reviewed'
                            ? 'mdi-alert-circle-outline'
                            : 'mdi-circle'
                        }}</v-icon>
                        {{ selectedEvidenceForDetails.status }}
                      </v-chip>
                      <!-- Compliance Score Badge -->
                      <v-chip
                        v-if="selectedEvidenceForDetails.complianceScore != null"
                        class="ml-2"
                        small
                        outlined
                        :color="
                          selectedEvidenceForDetails.complianceScore >= 4
                            ? 'green'
                            : selectedEvidenceForDetails.complianceScore >= 3
                            ? 'amber darken-2'
                            : selectedEvidenceForDetails.complianceScore >= 1
                            ? 'deep-orange'
                            : 'red'
                        "
                      >
                        <v-icon x-small left>mdi-shield-check-outline</v-icon>
                        {{ selectedEvidenceForDetails.complianceScore }}/5
                      </v-chip>
                    </div>
                  </div>
                </div>

                <!-- Title Section -->
                <div class="detail-section-modern">
                  <div class="section-header">
                    <v-icon color="primary" class="mr-2">mdi-text</v-icon>
                    <span class="section-title">Title</span>
                  </div>
                  <div class="section-content">
                    {{ selectedEvidenceForDetails.title || 'N/A' }}
                  </div>
                </div>

                <!-- Description Section -->
                <div class="detail-section-modern">
                  <div class="section-header">
                    <v-icon color="primary" class="mr-2">mdi-text-long</v-icon>
                    <span class="section-title">Description</span>
                  </div>
                  <div class="section-content">
                    {{ selectedEvidenceForDetails.description || 'No description provided' }}
                  </div>
                </div>

                <!-- Type and Version Section -->
                <div class="details-grid-section">
                  <div class="detail-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-tag</v-icon>
                      Type
                    </div>
                    <div class="detail-card-value">
                      <v-chip v-if="selectedEvidenceForDetails.type" small color="primary" outlined>
                        {{ selectedEvidenceForDetails.type }}
                      </v-chip>
                      <span v-else class="text-muted">N/A</span>
                    </div>
                  </div>

                  <div class="detail-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-numeric</v-icon>
                      Version
                    </div>
                    <div class="detail-card-value">
                      <v-chip
                        v-if="selectedEvidenceForDetails.version"
                        small
                        color="secondary"
                        outlined
                      >
                        v{{ selectedEvidenceForDetails.version }}
                      </v-chip>
                      <span v-else class="text-muted">N/A</span>
                    </div>
                  </div>
                </div>

                <!-- New Fields Section (visible only when not Draft) -->
                <div
                  v-if="
                    selectedEvidenceForDetails.status &&
                      selectedEvidenceForDetails.status !== 'Draft'
                  "
                  class="detail-section-modern additional-fields-section"
                >
                  <div class="section-header">
                    <v-icon color="primary" class="mr-2">mdi-information-variant</v-icon>
                    <span class="section-title">Additional Information</span>
                  </div>

                  <div class="details-grid-section mt-3">
                    <!-- Quality -->
                    <div class="detail-card pct-detail-card">
                      <div class="detail-card-label">
                        <v-icon small color="amber darken-2" class="mr-2">mdi-star</v-icon>
                        Quality
                      </div>
                      <template
                        v-if="
                          selectedEvidenceForDetails.quality != null &&
                            selectedEvidenceForDetails.quality !== ''
                        "
                      >
                        <div class="pct-bar-row">
                          <v-progress-linear
                            :value="Number(selectedEvidenceForDetails.quality)"
                            rounded
                            height="8"
                            :color="pctColor('quality', Number(selectedEvidenceForDetails.quality))"
                            background-color="grey lighten-3"
                          ></v-progress-linear>
                          <div class="pct-bar-labels">
                            <span class="pct-value">{{ selectedEvidenceForDetails.quality }}%</span>
                            <v-chip
                              x-small
                              dark
                              :color="
                                pctColor('quality', Number(selectedEvidenceForDetails.quality))
                              "
                              >{{
                                pctLabel('quality', Number(selectedEvidenceForDetails.quality))
                              }}</v-chip
                            >
                          </div>
                          <div v-if="selectedEvidenceForDetails.qualityNote" class="pct-note-text">
                            <v-icon x-small color="grey" class="mr-1">mdi-text</v-icon
                            >{{ selectedEvidenceForDetails.qualityNote }}
                          </div>
                        </div>
                      </template>
                      <div v-else class="detail-card-value text-muted">N/A</div>
                    </div>

                    <!-- Provenance -->
                    <div class="detail-card pct-detail-card">
                      <div class="detail-card-label">
                        <v-icon small color="blue darken-2" class="mr-2">mdi-source-branch</v-icon>
                        Provenance
                      </div>
                      <template
                        v-if="
                          selectedEvidenceForDetails.provenance != null &&
                            selectedEvidenceForDetails.provenance !== ''
                        "
                      >
                        <div class="pct-bar-row">
                          <v-progress-linear
                            :value="Number(selectedEvidenceForDetails.provenance)"
                            rounded
                            height="8"
                            :color="
                              pctColor('provenance', Number(selectedEvidenceForDetails.provenance))
                            "
                            background-color="grey lighten-3"
                          ></v-progress-linear>
                          <div class="pct-bar-labels">
                            <span class="pct-value"
                              >{{ selectedEvidenceForDetails.provenance }}%</span
                            >
                            <v-chip
                              x-small
                              dark
                              :color="
                                pctColor(
                                  'provenance',
                                  Number(selectedEvidenceForDetails.provenance),
                                )
                              "
                              >{{
                                pctLabel(
                                  'provenance',
                                  Number(selectedEvidenceForDetails.provenance),
                                )
                              }}</v-chip
                            >
                          </div>
                          <div
                            v-if="selectedEvidenceForDetails.provenanceNote"
                            class="pct-note-text"
                          >
                            <v-icon x-small color="grey" class="mr-1">mdi-text</v-icon
                            >{{ selectedEvidenceForDetails.provenanceNote }}
                          </div>
                        </div>
                      </template>
                      <div v-else class="detail-card-value text-muted">N/A</div>
                    </div>

                    <!-- Uncertainty -->
                    <div class="detail-card pct-detail-card">
                      <div class="detail-card-label">
                        <v-icon small color="deep-orange darken-1" class="mr-2"
                          >mdi-help-circle</v-icon
                        >
                        Uncertainty
                      </div>
                      <template
                        v-if="
                          selectedEvidenceForDetails.uncertainty != null &&
                            selectedEvidenceForDetails.uncertainty !== ''
                        "
                      >
                        <div class="pct-bar-row">
                          <v-progress-linear
                            :value="Number(selectedEvidenceForDetails.uncertainty)"
                            rounded
                            height="8"
                            :color="
                              pctColor(
                                'uncertainty',
                                Number(selectedEvidenceForDetails.uncertainty),
                              )
                            "
                            background-color="grey lighten-3"
                          ></v-progress-linear>
                          <div class="pct-bar-labels">
                            <span class="pct-value"
                              >{{ selectedEvidenceForDetails.uncertainty }}%</span
                            >
                            <v-chip
                              x-small
                              dark
                              :color="
                                pctColor(
                                  'uncertainty',
                                  Number(selectedEvidenceForDetails.uncertainty),
                                )
                              "
                              >{{
                                pctLabel(
                                  'uncertainty',
                                  Number(selectedEvidenceForDetails.uncertainty),
                                )
                              }}</v-chip
                            >
                          </div>
                          <div
                            v-if="selectedEvidenceForDetails.uncertaintyNote"
                            class="pct-note-text"
                          >
                            <v-icon x-small color="grey" class="mr-1">mdi-text</v-icon
                            >{{ selectedEvidenceForDetails.uncertaintyNote }}
                          </div>
                        </div>
                      </template>
                      <div v-else class="detail-card-value text-muted">N/A</div>
                    </div>

                    <!-- Assumption -->
                    <div class="detail-card pct-detail-card">
                      <div class="detail-card-label">
                        <v-icon small color="purple darken-1" class="mr-2"
                          >mdi-thought-bubble</v-icon
                        >
                        Assumption
                      </div>
                      <template
                        v-if="
                          selectedEvidenceForDetails.assumption != null &&
                            selectedEvidenceForDetails.assumption !== ''
                        "
                      >
                        <div class="pct-bar-row">
                          <v-progress-linear
                            :value="Number(selectedEvidenceForDetails.assumption)"
                            rounded
                            height="8"
                            :color="
                              pctColor('assumption', Number(selectedEvidenceForDetails.assumption))
                            "
                            background-color="grey lighten-3"
                          ></v-progress-linear>
                          <div class="pct-bar-labels">
                            <span class="pct-value"
                              >{{ selectedEvidenceForDetails.assumption }}%</span
                            >
                            <v-chip
                              x-small
                              dark
                              :color="
                                pctColor(
                                  'assumption',
                                  Number(selectedEvidenceForDetails.assumption),
                                )
                              "
                              >{{
                                pctLabel(
                                  'assumption',
                                  Number(selectedEvidenceForDetails.assumption),
                                )
                              }}</v-chip
                            >
                          </div>
                          <div
                            v-if="selectedEvidenceForDetails.assumptionNote"
                            class="pct-note-text"
                          >
                            <v-icon x-small color="grey" class="mr-1">mdi-text</v-icon
                            >{{ selectedEvidenceForDetails.assumptionNote }}
                          </div>
                        </div>
                      </template>
                      <div v-else class="detail-card-value text-muted">N/A</div>
                    </div>
                  </div>
                </div>

                <!-- File/Endpoint Section -->
                <div
                  v-if="
                    selectedEvidenceForDetails.fileName &&
                      selectedEvidenceForDetails.fileName !== 'null'
                  "
                  class="detail-section-modern file-section"
                >
                  <div class="section-header">
                    <v-icon color="primary" class="mr-2">{{
                      isToolTypeEvidence(selectedEvidenceForDetails)
                        ? 'mdi-link-variant'
                        : 'mdi-file-document'
                    }}</v-icon>
                    <span class="section-title">
                      {{
                        isToolTypeEvidence(selectedEvidenceForDetails)
                          ? 'Endpoint URL'
                          : 'Attached File'
                      }}
                    </span>
                  </div>
                  <div class="section-content file-content">
                    <div class="file-info">
                      <v-icon class="mr-2" color="grey darken-1">{{
                        isToolTypeEvidence(selectedEvidenceForDetails)
                          ? 'mdi-link'
                          : 'mdi-paperclip'
                      }}</v-icon>
                      <span class="file-name">{{ selectedEvidenceForDetails.fileName }}</span>
                    </div>
                    <div class="d-flex align-center" style="gap: 8px;">
                      <v-btn
                        color="primary"
                        class="download-button"
                        @click="downloadFileFromDetails(selectedEvidenceForDetails)"
                      >
                        <v-icon left>{{
                          isToolTypeEvidence(selectedEvidenceForDetails)
                            ? 'mdi-open-in-new'
                            : 'mdi-download'
                        }}</v-icon>
                        {{ isToolTypeEvidence(selectedEvidenceForDetails) ? 'Open' : 'Download' }}
                      </v-btn>
                      <v-btn
                        v-if="
                          isToolTypeEvidence(selectedEvidenceForDetails) &&
                            selectedEvidenceForDetails.source === 'Dependency Track' &&
                            (isReviewer || isCertifaiUser)
                        "
                        color="teal"
                        dark
                        :loading="testingEndpoint"
                        @click="testDependencyTrackEndpoint(selectedEvidenceForDetails)"
                      >
                        <v-icon left>mdi-play-circle-outline</v-icon>
                        Test Endpoint
                      </v-btn>
                    </div>
                  </div>

                  <!-- Dependency Track Endpoint Test Results -->
                  <div v-if="endpointTestResult" class="endpoint-test-result mt-3">
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        :color="endpointTestResult.status === 'ok' ? 'green' : 'red'"
                        class="mr-2"
                      >
                        {{
                          endpointTestResult.status === 'ok'
                            ? 'mdi-check-circle'
                            : 'mdi-alert-circle'
                        }}
                      </v-icon>
                      <span class="font-weight-bold">
                        {{
                          endpointTestResult.status === 'ok'
                            ? 'Endpoint is reachable'
                            : 'Endpoint error'
                        }}
                      </span>
                      <v-chip
                        x-small
                        class="ml-2"
                        :color="endpointTestResult.status === 'ok' ? 'green' : 'red'"
                        dark
                      >
                        HTTP {{ endpointTestResult.statusCode }}
                      </v-chip>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small @click="endpointTestResult = null">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div
                      v-if="endpointTestResult.message && endpointTestResult.status !== 'ok'"
                      class="text-body-2 red--text mb-2"
                    >
                      {{ endpointTestResult.message }}
                    </div>
                    <div v-if="endpointTestResult.body" class="endpoint-response-body">
                      <div class="d-flex align-center mb-1">
                        <span class="text-caption font-weight-bold grey--text text--darken-1"
                          >Response Body</span
                        >
                        <v-spacer></v-spacer>
                        <v-btn
                          x-small
                          text
                          color="primary"
                          @click="endpointResponseExpanded = !endpointResponseExpanded"
                        >
                          {{ endpointResponseExpanded ? 'Collapse' : 'Expand' }}
                        </v-btn>
                      </div>
                      <pre
                        class="endpoint-response-pre"
                        :class="{ 'endpoint-response-expanded': endpointResponseExpanded }"
                        >{{ formatEndpointResponse(endpointTestResult.body) }}</pre
                      >
                    </div>
                  </div>
                </div>

                <!-- Metadata Section -->
                <div class="details-grid-section">
                  <div class="detail-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-account</v-icon>
                      Created By
                    </div>
                    <div class="detail-card-value">
                      {{ selectedEvidenceForDetails.username || 'N/A' }}
                    </div>
                  </div>

                  <div class="detail-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-calendar-plus</v-icon>
                      Created At
                    </div>
                    <div class="detail-card-value">
                      {{ formatDate(selectedEvidenceForDetails.createdAt) }}
                    </div>
                  </div>

                  <div v-if="selectedEvidenceForDetails.updatedAt" class="detail-card">
                    <div class="detail-card-label">
                      <v-icon small color="primary" class="mr-2">mdi-calendar-edit</v-icon>
                      Last Updated
                    </div>
                    <div class="detail-card-value">
                      {{ formatDate(selectedEvidenceForDetails.updatedAt) }}
                    </div>
                  </div>
                </div>

                <!-- Reviewer Comments Section -->
                <div
                  v-if="
                    selectedEvidenceForDetails.comments &&
                      (selectedEvidenceForDetails.status === 'Reviewed' ||
                        selectedEvidenceForDetails.status === 'Rejected' ||
                        selectedEvidenceForDetails.status === 'Largely Compliant')
                  "
                  class="detail-section-modern comments-section"
                  :class="{
                    'rejected-section': selectedEvidenceForDetails.status === 'Rejected',
                    'reviewed-section':
                      selectedEvidenceForDetails.status === 'Reviewed' ||
                      selectedEvidenceForDetails.status === 'Largely Compliant',
                  }"
                >
                  <div class="section-header">
                    <v-icon
                      :color="
                        selectedEvidenceForDetails.status === 'Rejected' ? 'error' : 'warning'
                      "
                      class="mr-2"
                    >
                      {{
                        selectedEvidenceForDetails.status === 'Rejected'
                          ? 'mdi-alert-circle'
                          : 'mdi-comment-text'
                      }}
                    </v-icon>
                    <span class="section-title">
                      {{
                        selectedEvidenceForDetails.status === 'Rejected'
                          ? 'Rejection Reason'
                          : selectedEvidenceForDetails.status === 'Largely Compliant'
                          ? 'Reviewer Notes'
                          : 'Reviewer Comments'
                      }}
                    </span>
                    <v-chip
                      v-if="selectedEvidenceForDetails.status === 'Reviewed'"
                      x-small
                      color="error"
                      dark
                      class="ml-2"
                    >
                      <v-icon x-small left>mdi-alert</v-icon>
                      ACTION REQUIRED
                    </v-chip>
                  </div>
                  <v-alert
                    :type="
                      selectedEvidenceForDetails.status === 'Rejected'
                        ? 'error'
                        : selectedEvidenceForDetails.status === 'Largely Compliant'
                        ? 'success'
                        : 'info'
                    "
                    colored-border
                    border="left"
                    class="comments-alert"
                    :class="{ 'rejected-alert': selectedEvidenceForDetails.status === 'Rejected' }"
                  >
                    <div class="comment-text">
                      <v-icon
                        v-if="selectedEvidenceForDetails.status === 'Rejected'"
                        color="error"
                        class="mr-2"
                        style="vertical-align: middle;"
                      >
                        mdi-close-circle-outline
                      </v-icon>
                      {{ selectedEvidenceForDetails.comments }}
                    </div>
                  </v-alert>
                </div>

                <!-- SoCs Section -->
                <div
                  v-if="selectedEvidenceSocs.length > 0"
                  class="detail-section-modern socs-section"
                >
                  <div class="section-header">
                    <v-icon color="primary" class="mr-2">mdi-certificate</v-icon>
                    <span class="section-title">Mapped Subjects of Certification</span>
                  </div>
                  <div class="section-content">
                    <v-chip
                      v-for="soc in selectedEvidenceSocs"
                      :key="soc.id"
                      class="ma-1"
                      color="primary"
                      outlined
                      small
                    >
                      <v-icon left small>mdi-certificate</v-icon>
                      {{ soc.name }}
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon right x-small v-bind="attrs" v-on="on" class="ml-1">
                            mdi-information
                          </v-icon>
                        </template>
                        <div>
                          <div><strong>Standard Family:</strong> {{ soc.standardFamily }}</div>
                          <div><strong>Type:</strong> {{ soc.type }}</div>
                        </div>
                      </v-tooltip>
                    </v-chip>
                  </div>
                </div>
                <div v-else class="detail-section-modern">
                  <div class="section-header">
                    <v-icon color="grey" class="mr-2">mdi-certificate-outline</v-icon>
                    <span class="section-title">Mapped Subjects of Certification</span>
                  </div>
                  <div class="section-content grey--text">
                    N/A - No Subjects of Certification mapped to this evidence
                  </div>
                </div>
              </v-card-text>

              <v-card-text v-else class="pt-4">
                <v-alert type="warning" prominent>
                  <v-icon slot="prepend" large>mdi-alert</v-icon>
                  No evidence details available.
                </v-alert>
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions class="evidence-details-footer">
                <v-spacer></v-spacer>
                <v-btn color="grey darken-1" text large @click="evidenceDetailsDialog = false">
                  <v-icon left>mdi-close</v-icon>
                  Close
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- SubClaims Dialog -->
          <v-dialog v-model="subClaimsDialog" max-width="600">
            <v-card>
              <v-card-title>
                <span class="headline">Mapped Sub-Claims</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="subClaimsDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div
                  v-if="
                    selectedEvidence &&
                      selectedEvidence.subClaimsDto &&
                      selectedEvidence.subClaimsDto.length
                  "
                >
                  <v-list dense>
                    <v-list-item
                      v-for="subClaim in selectedEvidence.subClaimsDto"
                      :key="subClaim.id"
                      class="mb-2"
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          <strong>{{ subClaim.title || 'No Title' }}</strong>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <div><strong>ID:</strong> {{ subClaim.id || 'N/A' }}</div>
                          <div>
                            <strong>Description:</strong> {{ subClaim.description || 'N/A' }}
                          </div>
                          <div><strong>Status:</strong> {{ subClaim.status || 'N/A' }}</div>
                          <div><strong>Created At:</strong> {{ subClaim.createdAt || 'N/A' }}</div>
                          <div><strong>Updated At:</strong> {{ subClaim.updatedAt || 'N/A' }}</div>
                          <div><strong>Username:</strong> {{ subClaim.username || 'N/A' }}</div>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <div v-else>
                  <v-alert type="info" dense>
                    No sub-claims mapped to this evidence yet.
                  </v-alert>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="subClaimsDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- SoCs Dialog -->
          <v-dialog v-model="socsDialog" max-width="600">
            <v-card>
              <v-card-title>
                <span class="headline">Mapped Subjects of Certification</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="socsDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <div
                  v-if="
                    selectedEvidence && selectedEvidence.socIds && selectedEvidence.socIds.length
                  "
                >
                  <v-list dense>
                    <v-list-item
                      v-for="socId in selectedEvidence.socIds"
                      :key="typeof socId === 'object' ? socId.id : socId"
                      class="mb-2"
                    >
                      <v-list-item-avatar>
                        <v-icon color="primary">mdi-certificate</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content
                        v-if="getSocById(typeof socId === 'object' ? socId.id : socId)"
                      >
                        <v-list-item-title>
                          <strong>{{
                            getSocById(typeof socId === 'object' ? socId.id : socId).name
                          }}</strong>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <div>
                            <strong>ID:</strong> {{ typeof socId === 'object' ? socId.id : socId }}
                          </div>
                          <div>
                            <strong>Standard Family:</strong>
                            {{
                              getSocById(typeof socId === 'object' ? socId.id : socId)
                                .standardFamily
                            }}
                          </div>
                          <div>
                            <strong>Type:</strong>
                            {{ getSocById(typeof socId === 'object' ? socId.id : socId).type }}
                          </div>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
                <div v-else>
                  <v-alert type="info" dense>
                    No SoCs mapped to this evidence yet.
                  </v-alert>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="socsDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-model="deleteClicked" max-width="500">
            <v-card>
              <v-card-title>{{ $t('delete.confirm') }}</v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="deleteClicked = false">{{ $t('cancel') }}</v-btn>
                <v-btn color="red" text @click="deleteEvidence">{{ $t('delete.message') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="reviewClicked" max-width="600" scrollable>
            <v-card class="review-dialog-card">
              <!-- Header -->
              <div class="review-dialog-header">
                <v-icon color="white" size="22" class="mr-2">mdi-clipboard-text-search</v-icon>
                <span class="review-dialog-title"
                  >{{ $t('review.message') }} {{ $tc('evidence.title', 1) }}</span
                >
                <v-spacer></v-spacer>
                <v-btn icon dark small @click="handleCancel">
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </div>

              <v-card-text class="review-dialog-body pa-4">
                <!-- File section -->
                <div
                  v-if="reviewItem && reviewItem.fileName && reviewItem.fileName !== 'null'"
                  class="review-file-section mb-4"
                >
                  <v-alert type="warning" dense outlined class="mb-2 text-caption">
                    <strong>{{ $t('review.note') }}</strong>
                  </v-alert>
                  <div class="d-flex align-center justify-space-between">
                    <span class="grey--text text--darken-2 text-caption">
                      <v-icon small class="mr-1">{{
                        isToolTypeEvidence(reviewItem) ? 'mdi-link-variant' : 'mdi-file-document'
                      }}</v-icon>
                      {{ reviewItem.fileName }}
                    </span>
                    <v-btn small color="primary" @click="downloadFile()">
                      <v-icon small left>{{
                        isToolTypeEvidence(reviewItem) ? 'mdi-open-in-new' : 'mdi-download'
                      }}</v-icon>
                      {{ isToolTypeEvidence(reviewItem) ? 'Open' : $t('download') }}
                    </v-btn>
                  </div>
                </div>

                <!-- Verdict selection -->
                <div class="d-flex align-center mb-2">
                  <v-icon small color="primary" class="mr-1">mdi-scale-balance</v-icon>
                  <span class="font-weight-bold text-subtitle-2 primary--text"
                    >Select Compliance Verdict</span
                  >
                </div>

                <div class="verdict-grid">
                  <!-- Approve -->
                  <div
                    class="verdict-card"
                    :class="{ 'verdict-selected-approve': selectedRadio === 'approve' }"
                    @click="
                      selectedRadio = 'approve';
                      reviewScore = null;
                    "
                  >
                    <div class="verdict-icon-bg verdict-approve-bg">
                      <v-icon color="white" size="18">mdi-check-circle</v-icon>
                    </div>
                    <div class="verdict-info">
                      <div class="verdict-name">Approve</div>
                      <div class="verdict-score-label">Score 5/5 · Fully Compliant</div>
                      <div class="verdict-hint">All sufficiency rules met</div>
                    </div>
                    <div class="verdict-badge verdict-badge-approve">5</div>
                  </div>

                  <!-- Largely Compliant -->
                  <div
                    class="verdict-card"
                    :class="{ 'verdict-selected-largely': selectedRadio === 'largely' }"
                    @click="
                      selectedRadio = 'largely';
                      reviewScore = null;
                    "
                  >
                    <div class="verdict-icon-bg verdict-largely-bg">
                      <v-icon color="white" size="18">mdi-check-decagram-outline</v-icon>
                    </div>
                    <div class="verdict-info">
                      <div class="verdict-name">Largely Compliant</div>
                      <div class="verdict-score-label">Score 3–4/5</div>
                      <div class="verdict-hint">Most rules met; minor gaps</div>
                    </div>
                    <div class="verdict-badge verdict-badge-largely">3–4</div>
                  </div>

                  <!-- Partially Compliant -->
                  <div
                    class="verdict-card"
                    :class="{ 'verdict-selected-partial': selectedRadio === 'comment' }"
                    @click="
                      selectedRadio = 'comment';
                      reviewScore = null;
                    "
                  >
                    <div class="verdict-icon-bg verdict-partial-bg">
                      <v-icon color="white" size="18">mdi-alert-circle-outline</v-icon>
                    </div>
                    <div class="verdict-info">
                      <div class="verdict-name">Partially Compliant</div>
                      <div class="verdict-score-label">Score 1–2/5</div>
                      <div class="verdict-hint">Significant gaps; feedback required</div>
                    </div>
                    <div class="verdict-badge verdict-badge-partial">1–2</div>
                  </div>

                  <!-- Reject -->
                  <div
                    class="verdict-card"
                    :class="{ 'verdict-selected-reject': selectedRadio === 'reject' }"
                    @click="
                      selectedRadio = 'reject';
                      reviewScore = null;
                    "
                  >
                    <div class="verdict-icon-bg verdict-reject-bg">
                      <v-icon color="white" size="18">mdi-close-circle</v-icon>
                    </div>
                    <div class="verdict-info">
                      <div class="verdict-name">Reject</div>
                      <div class="verdict-score-label">Score 0/5 · Non-Compliant</div>
                      <div class="verdict-hint">No valid evidence; reason required</div>
                    </div>
                    <div class="verdict-badge verdict-badge-reject">0</div>
                  </div>
                </div>

                <!-- Score fine-tuning for Largely Compliant -->
                <div v-if="selectedRadio === 'largely'" class="score-picker-row mt-3">
                  <v-icon small color="amber darken-2" class="mr-2">mdi-star</v-icon>
                  <span class="text-caption font-weight-bold mr-3">Precise score:</span>
                  <v-btn-toggle v-model="reviewScore" dense mandatory color="amber darken-2">
                    <v-btn :value="3" small class="score-toggle-btn">
                      <v-icon x-small left>mdi-numeric-3</v-icon>3/5
                    </v-btn>
                    <v-btn :value="4" small class="score-toggle-btn">
                      <v-icon x-small left>mdi-numeric-4</v-icon>4/5
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <!-- Score fine-tuning for Partially Compliant -->
                <div v-if="selectedRadio === 'comment'" class="score-picker-row mt-3">
                  <v-icon small color="deep-orange" class="mr-2">mdi-star-half-full</v-icon>
                  <span class="text-caption font-weight-bold mr-3">Precise score:</span>
                  <v-btn-toggle v-model="reviewScore" dense mandatory color="deep-orange">
                    <v-btn :value="1" small class="score-toggle-btn">
                      <v-icon x-small left>mdi-numeric-1</v-icon>1/5
                    </v-btn>
                    <v-btn :value="2" small class="score-toggle-btn">
                      <v-icon x-small left>mdi-numeric-2</v-icon>2/5
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <!-- Comments textarea -->
                <v-textarea
                  v-if="selectedRadio && selectedRadio !== 'approve'"
                  v-model="reviewComments"
                  :label="
                    selectedRadio === 'reject'
                      ? $t('review.rejectReason')
                      : selectedRadio === 'largely'
                      ? $t('review.largelyNotes')
                      : $t('review.partialFeedback')
                  "
                  :hint="
                    selectedRadio === 'reject'
                      ? $t('review.rejectHint')
                      : $t('review.reviewMessageHint')
                  "
                  :required="selectedRadio !== 'largely'"
                  auto-grow
                  rows="2"
                  max-rows="5"
                  outlined
                  dense
                  class="mt-3"
                  :placeholder="selectedRadio === 'largely' ? $t('review.largelyPlaceholder') : ''"
                ></v-textarea>
              </v-card-text>

              <v-divider></v-divider>
              <v-card-actions class="review-dialog-footer">
                <v-btn text @click="handleCancel()">
                  {{ $t('cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  :disabled="reviewSubmitDisabled"
                  :color="buttonColor"
                  :loading="loading"
                  @click="handleReview"
                >
                  <v-icon left small>mdi-send</v-icon>
                  {{ $t('review.submitVerdict') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import Loader from '@/components/helpers/Loader.vue';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import evidenceMethods from './evidenceMethods';
import evidenceApi from '../api/evidenceApi';
import Soc from '@/modules/soc/api/soc.js';

export default {
  name: 'EvidenceList',
  components: {
    Loader,
    Breadcrumbs,
  },
  mixins: [util],
  props: {
    mapper: {
      type: Boolean,
      required: false,
    },
    disabledItems: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    isReviewer() {
      // Check in Vuex store - This is likely the most reliable source
      const storeRoles = this.$store?.getters?.roles || [];
      console.log('Store roles:', storeRoles);

      if (Array.isArray(storeRoles)) {
        if (storeRoles.some(role => String(role).toLowerCase() === 'reviewer')) {
          console.log('Found reviewer in store roles array');
          return true;
        }
      } else if (typeof storeRoles === 'string') {
        if (storeRoles.toLowerCase().includes('reviewer')) {
          console.log('Found reviewer in store roles string');
          return true;
        }
      }

      // 2. Check user object from localStorage - Another common place
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          console.log('User object:', user);

          if (user.roles) {
            if (Array.isArray(user.roles)) {
              if (user.roles.some(role => String(role).toLowerCase() === 'reviewer')) {
                console.log('Found reviewer in localStorage user.roles array');
                return true;
              }
            } else if (typeof user.roles === 'string') {
              if (user.roles.toLowerCase().includes('reviewer')) {
                console.log('Found reviewer in localStorage user.roles string');
                return true;
              }
            }
          }
        }
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }

      // 3. Directly check localStorage.roles - Some implementations put roles here
      const localStorageRoles = localStorage.getItem('roles');
      if (localStorageRoles) {
        console.log('localStorage.roles:', localStorageRoles);
        try {
          // Try to parse it as JSON
          const parsedRoles = JSON.parse(localStorageRoles);
          if (Array.isArray(parsedRoles)) {
            if (parsedRoles.some(role => String(role).toLowerCase() === 'reviewer')) {
              console.log('Found reviewer in parsed localStorage.roles array');
              return true;
            }
          } else if (typeof parsedRoles === 'string') {
            if (parsedRoles.toLowerCase().includes('reviewer')) {
              console.log('Found reviewer in parsed localStorage.roles string');
              return true;
            }
          }
        } catch (e) {
          // If not JSON, check it directly as a string
          if (localStorageRoles.toLowerCase().includes('reviewer')) {
            console.log('Found reviewer in localStorage.roles string');
            return true;
          }
        }
      }

      // 4. Simple JWT token check
      try {
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (token) {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join(''),
          );

          console.log('JWT payload:', jsonPayload);

          // Simple string search in the entire token payload
          if (jsonPayload.toLowerCase().includes('reviewer')) {
            console.log('Found reviewer string in JWT payload');
            return true;
          }
        }
      } catch (e) {
        console.error('Error checking JWT token:', e);
      }

      // If all checks fail, log what we found and return false
      console.log('No reviewer role found anywhere');

      return false;
    },
    isCertifaiUser() {
      // Check in Vuex store
      const storeRoles = this.$store?.getters?.roles || [];

      if (Array.isArray(storeRoles)) {
        if (storeRoles.some(role => String(role).toLowerCase() === 'certifai-user')) {
          return true;
        }
      } else if (typeof storeRoles === 'string') {
        if (storeRoles.toLowerCase().includes('certifai-user')) {
          return true;
        }
      }

      // Check localStorage.roles
      const localStorageRoles = localStorage.getItem('roles');
      if (localStorageRoles) {
        try {
          const parsedRoles = JSON.parse(localStorageRoles);
          if (Array.isArray(parsedRoles)) {
            if (parsedRoles.some(role => String(role).toLowerCase() === 'certifai-user')) {
              return true;
            }
          } else if (typeof parsedRoles === 'string') {
            if (parsedRoles.toLowerCase().includes('certifai-user')) {
              return true;
            }
          }
        } catch (e) {
          if (localStorageRoles.toLowerCase().includes('certifai-user')) {
            return true;
          }
        }
      }

      return false;
    },
    buttonColor() {
      switch (this.selectedRadio) {
        case 'reject':
          return 'red';
        case 'comment':
          return 'deep-orange';
        case 'largely':
          return 'amber darken-2';
        case 'approve':
          return 'green';
        default:
          return 'gray';
      }
    },
    reviewSubmitDisabled() {
      if (!this.selectedRadio) return true;
      if (this.selectedRadio === 'largely' && !this.reviewScore) return true;
      if (
        this.selectedRadio === 'comment' &&
        (!this.reviewScore || !this.reviewComments || !this.reviewComments.trim())
      )
        return true;
      if (this.selectedRadio === 'reject' && (!this.reviewComments || !this.reviewComments.trim()))
        return true;
      return false;
    },
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('evidence.table-footer');
    },
    regularEvidence() {
      return this.evidences.filter(evidence => !this.isThreatSpiderEvidence(evidence));
    },
    threatSpiderEvidence() {
      return this.evidences.filter(evidence => this.isThreatSpiderEvidence(evidence));
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('evidence.headers.id'), value: 'id' },
        { text: this.$t('evidence.headers.name'), value: 'title' },
        { text: this.$t('evidence.headers.description'), value: 'description' },
        { text: this.$t('evidence.headers.type'), value: 'type' },
        { text: this.$t('evidence.headers.status'), value: 'status' },
        { text: this.$t('evidence.headers.version'), value: 'version' },
      ];
    },
    filteredRegularEvidence() {
      let result = this.regularEvidence;

      if (this.searchQuery) {
        result = result.filter(evidence => this.customFilter(null, this.searchQuery, evidence));
      }

      return this.sortEvidenceItems(result);
    },
    filteredThreatSpiderEvidence() {
      let result = this.threatSpiderEvidence;

      if (this.searchQuery) {
        result = result.filter(evidence => this.customFilter(null, this.searchQuery, evidence));
      }

      return this.sortEvidenceItems(result);
    },
    paginatedRegularEvidence() {
      const start = (this.regularEvidencePage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredRegularEvidence.slice(start, end);
    },
    paginatedThreatSpiderEvidence() {
      const start = (this.threatSpiderEvidencePage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredThreatSpiderEvidence.slice(start, end);
    },
    getHeaders() {
      const t = this.$t.bind(this);
      const headers = [
        {
          text: t('evidence.headers.id'),
          value: 'id',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: t('evidence.headers.name'),
          value: 'title',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('evidence.headers.description'),
          value: 'description',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '20%',
        },
        {
          text: t('evidence.headers.type'),
          value: 'type',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '7%',
        },
        {
          text: t('evidence.headers.version'),
          value: 'version',
          sortable: false,
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: t('evidence.headers.createdAt'),
          value: 'createdAt',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('evidence.headers.status'),
          value: 'status',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('evidence.headers.updatedAt'),
          value: 'updatedAt',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('evidence.headers.actions'),
          value: 'actions',
          align: 'center',
          sortable: false,
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];

      // Add the selection column if in mapper view
      if (this.mapper) {
        headers.push({
          text: t('evidence.headers.selection'),
          value: 'switch',
          align: 'center',
          sortable: false,
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        });
      }

      return headers;
    },
    selectedEvidenceSocs() {
      if (!this.selectedEvidenceForDetails || !this.selectedEvidenceForDetails.socIds) {
        return [];
      }
      // Map socIds to full SoC objects from socList
      return this.selectedEvidenceForDetails.socIds
        .map(socIdObj => {
          // Handle both formats: array of objects with id property or array of ids
          const socId = typeof socIdObj === 'object' ? socIdObj.id : socIdObj;
          return this.socList.find(soc => soc.id === socId.toString());
        })
        .filter(soc => soc !== undefined);
    },
  },
  data() {
    return {
      loading: false,
      selectedComponents: [],
      evidences: [],
      searchQuery: '',
      searchField: 'all',
      deleteClicked: false,
      deleteItem: null,
      reviewClicked: false,
      reviewItem: null,
      deleteDialog: false,
      selectedRadio: null,
      reviewScore: null,
      rules: {
        required: value => !!value || 'Required.',
      },
      reviewComments: null,
      itemWithComments: {},
      activeTab: 0,
      regularEvidencePage: 1,
      threatSpiderEvidencePage: 1,
      itemsPerPage: 6,
      subClaimsDialog: false,
      selectedEvidence: null,
      evidenceDetailsDialog: false,
      selectedEvidenceForDetails: null,
      // Sorting variables
      sortField: 'updatedAt',
      sortDirection: 'desc',
      socsDialog: false,
      socList: [],
      testingEndpoint: false,
      endpointTestResult: null,
      endpointResponseExpanded: false,
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchEvidences();
    this.fetchSocs(); // Fetch SoCs when component is created
    this.$emit('loading', false);
    this.loading = false;
    if (this.$route.path === '/evidences') {
      localStorage.removeItem('subClaim');
    }
  },
  watch: {
    disabledItems() {
      this.selectedComponents = [];
    },
    sortField() {
      // Reset pagination when sort field changes
      this.regularEvidencePage = 1;
      this.threatSpiderEvidencePage = 1;
    },
  },
  // Methods can be found in /modules/evidence/views/evidenceMethods.js
  methods: {
    ...evidenceMethods,
    hasComments(evidence) {
      return (
        evidence.comments &&
        evidence.comments.trim() !== '' &&
        (evidence.status === 'Reviewed' ||
          evidence.status === 'Rejected' ||
          evidence.status === 'Largely Compliant')
      );
    },
    isThreatSpiderEvidence(evidence) {
      // Check based on source field
      return evidence.source === 'Threat Spider';
    },
    isToolTypeEvidence(evidence) {
      return (
        evidence &&
        (evidence.source === 'Dependency Track' ||
          evidence.source === 'Threat Spider' ||
          evidence.source === 'Threat Dragon JSON')
      );
    },
    pctColor(field, v) {
      if (field === 'quality' || field === 'provenance') {
        if (v >= 86) return 'green darken-2';
        if (v >= 61) return 'light-green darken-1';
        if (v >= 31) return 'amber darken-2';
        return 'red darken-1';
      }
      // uncertainty & assumption: lower is better
      if (v >= 76) return 'red darken-2';
      if (v >= 51) return 'deep-orange darken-1';
      if (v >= 26) return 'amber darken-2';
      return 'green darken-1';
    },
    pctLabel(field, v) {
      if (field === 'quality') {
        if (v >= 86) return 'Excellent';
        if (v >= 61) return 'High';
        if (v >= 31) return 'Medium';
        return 'Low';
      }
      if (field === 'provenance') {
        if (v >= 86) return 'Highly Trusted';
        if (v >= 61) return 'Trusted';
        if (v >= 31) return 'Moderate';
        return 'Questionable';
      }
      if (field === 'uncertainty') {
        if (v >= 76) return 'Very High';
        if (v >= 51) return 'High';
        if (v >= 26) return 'Moderate';
        return 'Low';
      }
      if (field === 'assumption') {
        if (v >= 76) return 'Heavily Assumed';
        if (v >= 51) return 'Many Assumptions';
        if (v >= 26) return 'Moderate';
        return 'Few Assumptions';
      }
      return '';
    },
    getSourceColor(source) {
      const colors = {
        Manual: 'blue',
        manual: 'blue',
        'Threat Spider': 'purple',
        'threat spider': 'purple',
        'Dependency Track': 'orange',
        'dependency track': 'orange',
        'Threat Dragon JSON': 'teal',
        'threat dragon json': 'teal',
      };
      return colors[source] || 'blue'; // Default to blue for Manual/undefined
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    },
    showSubClaimsDialog(evidence) {
      this.selectedEvidence = evidence;
      this.subClaimsDialog = true;
    },
    showEvidenceDetailsDialog(evidence) {
      this.selectedEvidenceForDetails = evidence;
      this.endpointTestResult = null;
      this.endpointResponseExpanded = false;
      this.evidenceDetailsDialog = true;
    },
    showSubClaimsFromDetails() {
      this.selectedEvidence = this.selectedEvidenceForDetails;
      this.evidenceDetailsDialog = false;
      this.subClaimsDialog = true;
    },
    editEvidenceFromDetails() {
      this.evidenceDetailsDialog = false;
      this.editEvidence(this.selectedEvidenceForDetails);
    },
    downloadFileFromDetails(evidence) {
      if (this.isToolTypeEvidence(evidence)) {
        window.open(evidence.fileName, '_blank');
      } else {
        this.reviewItem = evidence;
        evidenceMethods.downloadFile.call(this);
      }
    },
    async testDependencyTrackEndpoint(evidence) {
      this.testingEndpoint = true;
      this.endpointTestResult = null;
      this.endpointResponseExpanded = false;
      try {
        const response = await evidenceApi.testEndpoint(evidence.id);
        this.endpointTestResult = response.data;
      } catch (error) {
        const data = error.response?.data;
        if (data && typeof data === 'object' && data.status) {
          this.endpointTestResult = data;
        } else {
          this.endpointTestResult = {
            status: 'error',
            statusCode: error.response?.status || 0,
            message: data?.message || error.message || 'Failed to reach endpoint',
          };
        }
      } finally {
        this.testingEndpoint = false;
      }
    },
    formatEndpointResponse(body) {
      if (!body) return '';
      if (typeof body === 'string') {
        try {
          return JSON.stringify(JSON.parse(body), null, 2);
        } catch {
          return body;
        }
      }
      return JSON.stringify(body, null, 2);
    },
    async fetchSocs() {
      try {
        const response = await Soc.all();
        this.socList = response.data || [];
      } catch (error) {
        console.error('Error fetching SoCs:', error);
      }
    },
    // Sorting methods
    sortEvidenceItems(items) {
      return [...items].sort((a, b) => {
        if (this.sortField === 'status') {
          // Sort by status
          if (a.status < b.status) return this.sortDirection === 'asc' ? -1 : 1;
          if (a.status > b.status) return this.sortDirection === 'asc' ? 1 : -1;
          // If status is the same, sort by updatedAt as secondary criterion
          return this.sortByDate(b.updatedAt, a.updatedAt);
        } else {
          // Sort by date fields
          return this.sortByDate(a[this.sortField], b[this.sortField]);
        }
      });
    },
    sortByDate(dateA, dateB) {
      const dateValA = dateA ? new Date(dateA).getTime() : 0;
      const dateValB = dateB ? new Date(dateB).getTime() : 0;

      return this.sortDirection === 'asc' ? dateValA - dateValB : dateValB - dateValA; // Default to descending (newest first)
    },
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      // Reset pagination when sort direction changes
      this.regularEvidencePage = 1;
      this.threatSpiderEvidencePage = 1;
    },
    getSortIcon(field) {
      if (this.sortField !== field) return 'mdi-unfold-more-horizontal';
      return this.sortDirection === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip non-searchable fields and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
      this.regularEvidencePage = 1;
      this.threatSpiderEvidencePage = 1;
    },
    // Update downloadFile method to handle tool types
    downloadFile() {
      if (this.reviewItem && this.isToolTypeEvidence(this.reviewItem)) {
        // Open the URL in a new tab for tool types
        window.open(this.reviewItem.fileName, '_blank');
      } else if (this.itemWithComments && this.isToolTypeEvidence(this.itemWithComments)) {
        // Open the URL in a new tab for tool types in comments view
        window.open(this.itemWithComments.fileName, '_blank');
      } else {
        // Use original download logic for regular files
        evidenceMethods.downloadFile.call(this);
      }
    },
    showSocsDialog(evidence) {
      this.selectedEvidence = evidence;
      this.socsDialog = true;
    },
    getSocById(socId) {
      return this.socList.find(soc => soc.id === socId.toString()) || {};
    },
    getScoreColor(score) {
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      if (score >= 40) return 'orange';
      return 'error';
    },
  },
};
</script>

<style>
/* ============================================
   EVIDENCE LIST PAGE - Modern Design System
   Matching DashboardContent.vue & SBOM patterns
   ============================================ */

/* Breadcrumbs */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ============================================
   HERO CARD
   ============================================ */
.ev-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.ev-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.ev-hero-header-content {
  flex: 1;
}

.ev-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.ev-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.ev-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  margin: 0;
  font-weight: 400;
}

.ev-intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.05rem;
}

/* ============================================
   WORKFLOW SECTION
   ============================================ */
.ev-workflow-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 28px;
  margin-top: 8px;
}

.ev-section-header {
  display: flex;
  align-items: center;
}

.ev-workflow-steps {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.ev-ws {
  background: white;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.ev-ws:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.ev-ws.step-create:hover {
  background: linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%);
}

.ev-ws.step-review:hover {
  background: linear-gradient(135deg, #ffffff 0%, #fff3e0 100%);
}

.ev-ws.step-approve:hover {
  background: linear-gradient(135deg, #ffffff 0%, #e8f5e9 100%);
}

.ev-step-number {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
}

.ev-step-number.review-num {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.4);
}

.ev-step-number.approve-num {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4);
}

.ev-step-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0 16px 0;
  transition: all 0.3s ease;
}

.ev-step-icon-wrapper.create-icon {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  box-shadow: 0 8px 24px rgba(25, 118, 210, 0.3);
}

.ev-step-icon-wrapper.review-icon {
  background: linear-gradient(135deg, #ff8f00, #ffb74d);
  box-shadow: 0 8px 24px rgba(255, 143, 0, 0.3);
}

.ev-step-icon-wrapper.approve-icon {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
  box-shadow: 0 8px 24px rgba(46, 125, 50, 0.3);
}

.ev-ws:hover .ev-step-icon-wrapper {
  transform: scale(1.05);
}

.ev-step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 12px;
  line-height: 1.3;
}

.ev-step-description {
  color: #616161;
  font-size: 0.875rem;
  line-height: 1.6;
  flex-grow: 1;
  margin-bottom: 8px;
}

/* Arrow connectors */
.ev-workflow-col {
  position: relative;
}

.ev-workflow-col:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 12px solid transparent;
  border-bottom: 12px solid transparent;
  border-left: 14px solid #1976d2;
  z-index: 10;
  filter: drop-shadow(2px 0 4px rgba(25, 118, 210, 0.3));
}

@media (max-width: 960px) {
  .ev-workflow-col:not(:last-child)::after {
    display: none;
  }
}

/* ============================================
   FEATURE BOXES
   ============================================ */
.ev-feature-box {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  height: 100%;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.ev-feature-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #1976d2;
}

.ev-feature-box.lifecycle-box:hover {
  border-color: #4caf50;
}

.ev-feature-box-header {
  display: flex;
  align-items: center;
}

.ev-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ev-feature-list li {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
  color: #424242;
  font-size: 0.9rem;
  line-height: 1.5;
}

.ev-feature-list li:last-child {
  border-bottom: none;
}

/* ============================================
   METRIC CARDS
   ============================================ */
.ev-metric-card {
  height: 100%;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  transition: transform 0.3s, box-shadow 0.3s;
  overflow: hidden;
}

.ev-metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15) !important;
}

.ev-primary-grad {
  background: linear-gradient(135deg, #1a237e 0%, #5c6bc0 100%);
}

.ev-secondary-grad {
  background: linear-gradient(135deg, #00695c 0%, #4db6ac 100%);
}

.ev-accent-grad {
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%);
}

.ev-success-grad {
  background: linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%);
}

/* ============================================
   CONTENT WRAPPER & SEARCH BAR
   ============================================ */
.ev-content-wrapper {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.ev-search-bar-container {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.ev-search-bar-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 10px 20px;
  display: flex;
  align-items: center;
}

.ev-search-bar-body {
  padding: 16px 20px;
  gap: 12px;
  background: #fafafa;
}

.ev-create-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 40px !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3) !important;
}

/* ============================================
   MAPPER SWITCH & SELECTED INDICATOR
   ============================================ */
.ev-selected-chip {
  font-weight: 600;
  letter-spacing: 0.3px;
}

.ev-mapper-switch {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* ============================================
   TABS
   ============================================ */
.ev-tabs-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.ev-tabs {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%) !important;
}

/* ============================================
   EVIDENCE CARDS
   ============================================ */
.evidence-card {
  border-radius: 16px !important;
  border: 1px solid #e0e0e0;
  margin-bottom: 16px;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.evidence-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  border-color: #bbdefb;
}

.threat-spider-card {
  border-left: 4px solid #9c27b0;
}

.threat-spider-card:hover {
  border-color: #9c27b0;
}

.evidence-title {
  font-weight: 700;
  font-size: 1.1rem;
  line-height: 1.3;
  max-width: 70%;
  word-wrap: break-word;
  color: #1a237e;
}

.evidence-details {
  flex-grow: 1;
}

/* ============================================
   SORT CONTROLS
   ============================================ */
.sort-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.sort-controls .v-btn-toggle {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.sort-controls .v-btn {
  font-size: 0.8rem;
  text-transform: none;
  font-weight: 600;
}

.sort-controls .v-btn.v-item--active {
  background-color: rgba(25, 118, 210, 0.1);
}

.search-field {
  min-width: 150px;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 960px) {
  .ev-hero-header {
    flex-direction: column;
    text-align: center;
  }

  .ev-hero-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .ev-ws {
    margin-bottom: 24px;
  }
}

@media (max-width: 600px) {
  .ev-hero-title {
    font-size: 1.4rem;
  }

  .ev-workflow-section {
    padding: 20px 16px;
  }

  .ev-step-icon-wrapper {
    width: 64px;
    height: 64px;
  }
}

/* Evidence Details Dialog Styles */
.detail-section {
  margin-bottom: 16px;
}

.detail-label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 1rem;
  color: #333;
  word-wrap: break-word;
}

.v-dialog .v-card__title.primary {
  background-color: #1976d2 !important;
}

/* Modern Evidence Details Dialog Styles */
.evidence-details-card {
  border-radius: 12px;
  overflow: hidden;
}

.evidence-details-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 20px 24px;
}

.evidence-details-title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.evidence-details-content {
  padding: 24px;
  background-color: #f8f9fa;
}

/* Header Section */
.details-header-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

/* Detail Cards */
.detail-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.detail-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.detail-card-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.detail-card-value {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  word-wrap: break-word;
}

/* Modern Section Styles */
.detail-section-modern {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Grid Section */
.details-grid-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

/* File Section Specific Styles */
.file-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.file-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 200px;
  padding: 12px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.file-name {
  font-weight: 500;
  color: #333;
  word-break: break-all;
}

.download-button {
  min-width: 140px;
  padding: 8px 24px !important;
  height: 44px !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
  transition: all 0.2s ease;
}

.download-button:hover {
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  transform: translateY(-2px);
}

/* Endpoint Test Result */
.endpoint-test-result {
  background: #f5f7fa;
  border: 1px solid #e0e4ea;
  border-radius: 10px;
  padding: 14px 16px;
}
.endpoint-response-body {
  margin-top: 8px;
}
.endpoint-response-pre {
  background: #1a2332;
  color: #e0e6ed;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.78rem;
  line-height: 1.5;
  max-height: 150px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
}
.endpoint-response-expanded {
  max-height: 500px;
}

/* Comments Section */
.comments-section {
  transition: all 0.3s ease;
}

.reviewed-section {
  background: #fff8e1;
  border-left: 4px solid #ff9800;
}

.rejected-section {
  background: #ffebee;
  border-left: 4px solid #f44336;
  animation: pulse-border 2s ease-in-out infinite;
}

@keyframes pulse-border {
  0%,
  100% {
    border-left-color: #f44336;
  }
  50% {
    border-left-color: #d32f2f;
  }
}

.comments-alert {
  margin-bottom: 0;
  border-radius: 6px;
}

.rejected-alert {
  background-color: #ffcdd2 !important;
}

.rejected-alert .v-alert__content {
  font-weight: 500;
}

.comment-text {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #333;
  display: flex;
  align-items: flex-start;
}

.comment-text .v-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Status Chip */
.status-chip-large {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 8px 16px;
  height: auto;
}

/* Footer */
.evidence-details-footer {
  background: white;
  padding: 16px 24px;
}

/* Text Utilities */
.text-muted {
  color: #999;
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 600px) {
  .details-header-section,
  .details-grid-section {
    grid-template-columns: 1fr;
  }

  .file-content {
    flex-direction: column;
    align-items: stretch;
  }

  .download-button {
    width: 100%;
  }
}

/* Badge notification styling */
.v-badge__badge .v-icon {
  font-size: 12px;
  height: 12px;
  width: 12px;
}

.v-badge__badge {
  animation: pulse-badge 2s ease-in-out infinite;
}

@keyframes pulse-badge {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.85;
  }
}

/* Tooltip enhancement */
.v-tooltip__content {
  font-size: 0.875rem;
  padding: 8px 12px;
}

/* Additional Fields Section */
.additional-fields-section {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-left: 4px solid #2196f3;
}

/* Percentage indicator bars in detail cards */
.pct-detail-card {
  display: flex;
  flex-direction: column;
}

.pct-bar-row {
  margin-top: 6px;
}

.pct-bar-labels {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}

.pct-value {
  font-size: 1rem;
  font-weight: 700;
  color: #212121;
}

.pct-note-text {
  margin-top: 8px;
  font-size: 0.78rem;
  color: #546e7a;
  line-height: 1.5;
  font-style: italic;
  display: flex;
  align-items: flex-start;
  gap: 2px;
}

.socs-section {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
}

/* ============================================
   REVIEW DIALOG - Compliance Verdict Selector
   ============================================ */
.review-dialog-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.review-dialog-header {
  background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  color: white;
}

.review-dialog-title {
  font-size: 1rem;
  font-weight: 600;
}

.review-dialog-body {
  background: #f8f9fa;
}

.review-file-section {
  background: white;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.verdict-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.verdict-card {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  border: 2px solid #e0e0e0;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 10px;
}

.verdict-card:hover {
  border-color: #1976d2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

/* Selected states */
.verdict-selected-approve {
  border-color: #2e7d32 !important;
  background: #f1f8f2 !important;
}
.verdict-selected-largely {
  border-color: #f57f17 !important;
  background: #fffde7 !important;
}
.verdict-selected-partial {
  border-color: #e65100 !important;
  background: #fff3e0 !important;
}
.verdict-selected-reject {
  border-color: #c62828 !important;
  background: #ffebee !important;
}

.verdict-icon-bg {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.verdict-approve-bg {
  background: linear-gradient(135deg, #2e7d32, #66bb6a);
}
.verdict-largely-bg {
  background: linear-gradient(135deg, #f57f17, #ffb300);
}
.verdict-partial-bg {
  background: linear-gradient(135deg, #e65100, #ff8a65);
}
.verdict-reject-bg {
  background: linear-gradient(135deg, #c62828, #ef5350);
}

.verdict-info {
  flex: 1;
  min-width: 0;
}

.verdict-name {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1a237e;
  line-height: 1.2;
}

.verdict-score-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #757575;
  margin-top: 1px;
}

.verdict-hint {
  font-size: 0.66rem;
  color: #9e9e9e;
  line-height: 1.3;
  margin-top: 1px;
}

.verdict-badge {
  font-size: 0.65rem;
  font-weight: 800;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
}

.verdict-badge-approve {
  background: #2e7d32;
}
.verdict-badge-largely {
  background: #f57f17;
}
.verdict-badge-partial {
  background: #e65100;
}
.verdict-badge-reject {
  background: #c62828;
}

.score-picker-row {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  padding: 10px 14px;
  border: 1px solid #e0e0e0;
}

.score-toggle-btn {
  font-size: 0.75rem !important;
  text-transform: none !important;
}

.review-dialog-footer {
  background: white;
  padding: 10px 16px;
}
</style>
